/* eslint-disable @typescript-eslint/no-explicit-any */
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import { Button, Divider, Form, Modal, Space, Table } from 'antd';
import {
  DefaultRouteTabs,
  MultipleContactModal,
  SaveButton,
} from '@/components/_common';
import {
  useEmployee,
  useEmployeeContactCreate,
  useEmployeeContactDetailRemove,
  useEmployeeContactEdit,
  useEmployeeContactList,
  useEmployeeContactRemove,
} from '@/core/domain/enterprise/Employees/Employees.hooks';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { EmployeeContact } from '@/core/domain/enterprise/Employees/Employees.types';
import { ColumnsType } from 'antd/lib/table';
import { useDisclosure } from '@/hooks';
import { LegalPersonContactFormInterface } from '@/components/_common/MultipleContactModal/types';
import moment from 'moment';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { useEmployeeFormContext } from '@/components/Employee/EmployeeFormProvider';
import { getTableScrollX } from '@/utils/table';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { DateTimeFormat } from '@/utils/moment';
import { PageProps } from '@/types/PageProps';
import EmployeeFooter from '@/components/Employee/EmployeeFooter';
import { useContactColumns } from '@/hooks/useContactColumns';
import { removeMaskDetailList } from '@/utils/mask/removeMaskDetailList';

const EmployeeContactPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const [form] = Form.useForm<LegalPersonContactFormInterface>();
  const [formSubmit] = Form.useForm();
  const { setParamLabel } = useBreadcrumbs();
  const [modalVisible, modalActions] = useDisclosure(false, {
    onClose: () => form.resetFields(),
  });
  const { columns: contactColumns } = useContactColumns<EmployeeContact>();

  const { disabled, employeeId } = useEmployeeFormContext();

  const employee = useEmployee(employeeId);
  const employeeContacts = useEmployeeContactList(employeeId);
  const employeeContactCreate = useEmployeeContactCreate();
  const employeeContactEdit = useEmployeeContactEdit();
  const employeeContactRemove = useEmployeeContactRemove();
  const employeeContactDetailRemove = useEmployeeContactDetailRemove();

  useEffect(() => {
    if (employee.data) {
      setParamLabel(':employeeId', employee.data.name);
    }
  }, [employee.data]);

  useEffect(() => {
    if (employeeContacts.data?.length && modalVisible) {
      const contactUpdated = employeeContacts.data.find(
        (contact) => contact.id === form.getFieldValue('contactId'),
      );

      if (contactUpdated) {
        handleContactSelect(contactUpdated);
      }
    }
  }, [employeeContacts.data, modalVisible]);

  async function handleSave() {
    try {
      const { contactId, birthDate, ...values } = await form.validateFields();

      const obj = {
        ...values,
        employeeId: Number(employeeId),
        birthDate: birthDate ? birthDate.format(DateTimeFormat) : undefined,
        detailList: removeMaskDetailList(values.detailList),
      };

      if (contactId) {
        await employeeContactEdit.mutateAsync({ ...obj, id: contactId });
      } else {
        await employeeContactCreate.mutateAsync(obj);
        modalActions.close();
      }
    } catch {}
  }

  function handleContactRemove(employeeContact: EmployeeContact) {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.contact.remove',
      }),
      content: `${intl.formatMessage({
        id: 'customers.contact.remove.alert',
      })} ${employeeContact?.name}?`,
      onOk: () =>
        employeeContactRemove.mutateAsync({
          employeeId: employeeContact.employeeId,
          contactId: employeeContact.id,
        }),
      okText: intl.formatMessage({ id: 'commons.form.button.remove' }),
    });
  }

  async function handleRemoveContactDetail(detailId: number) {
    await employeeContactDetailRemove.mutateAsync({
      employeeId: Number(employeeId),
      detailId,
    });

    employeeContacts.refetch();
  }

  function handleContactSelect(employeeContact: EmployeeContact) {
    form.setFieldsValue({
      ...employeeContact,
      detailList: employeeContact.detailList,
      contactId: employeeContact.id,
      birthDate: employeeContact?.birthDate
        ? moment(employeeContact.birthDate)
        : undefined,
    });
  }

  const columns: ColumnsType<EmployeeContact> = [
    ...contactColumns,
    {
      key: 'action',
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      align: 'right',
      fixed: 'right',
      width: 50,
      render: (_, row: EmployeeContact) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                handleContactSelect(row);
                modalActions.open();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon onClick={() => handleContactRemove(row)} />
        </Space>
      ),
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      route={route.parent}
      baseKey={String(employeeId)}
      basePath={`/app/registry/employees/${employeeId}`}
      extra={
        <SpaceForButton>
          <Button block type="primary" onClick={modalActions.open}>
            {intl.formatMessage({ id: 'commons.entity.add' })}
          </Button>

          <SaveButton block onClick={formSubmit.submit} disabled={disabled}>
            Salvar
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Form form={formSubmit} name="employeeContacts" />

      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <Table
        dataSource={employeeContacts.data}
        rowKey={(row) => row.id}
        columns={columns}
        loading={employeeContacts.isLoading}
        pagination={false}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <EmployeeFooter disabled={disabled} form={form} />

      <MultipleContactModal
        open={modalVisible}
        onCancel={modalActions.close}
        onOk={handleSave}
        confirmLoading={
          employeeContactCreate.isLoading || employeeContactEdit.isLoading
        }
        form={form}
        personType="NaturalPerson"
        onDetailRemove={handleRemoveContactDetail}
      />
    </DefaultRouteTabs>
  );
};

export default EmployeeContactPage;
